'use client';

import { Checkbox } from '@curology/ui-components-web/react';
import { ProductCard } from '@curology/ui-components-web/react/Card/ProductCard';
import { ProductCardContent } from '@curology/ui-components-web/react/Card/ProductCard/ProductCardContent';
import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { AgencyLockIcon } from '@curology/ui-components-web/react/Icon/icons/AgencyLockIcon';
import { AgencyMinusIcon } from '@curology/ui-components-web/react/Icon/icons/AgencyMinusIcon';
import { AgencyPlusIcon } from '@curology/ui-components-web/react/Icon/icons/AgencyPlusIcon';
import { LockIcon } from '@curology/ui-components-web/react/Icon/icons/LockIcon';
import { MinusIcon } from '@curology/ui-components-web/react/Icon/icons/MinusIcon';
import { PlusIcon } from '@curology/ui-components-web/react/Icon/icons/PlusIcon';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useMemo } from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { RxCardPricing } from '@/components/product-selection/components/RxCardPricing/RxCardPricing';
import { useSelectedItemIds } from '@/components/product-selection/hooks/useSelectedItemIds';
import { DATA_KEYS } from '@/consts/dataKeys';
import type { Card } from '@/types/productSelectionAndCheckout/types';

import { useProductIngredientsModalState } from '../../../hooks/useProductIngredientsModalState';
import IngredientTabs from '../../ingredientTabs';
import ProductCardImageCarousel from '../../productCardImageCarousel';

const brandMinusIcons = {
  curology: MinusIcon,
  agency: AgencyMinusIcon,
} as const;

const brandPlusIcons = {
  curology: PlusIcon,
  agency: AgencyPlusIcon,
} as const;

const brandLockIcons = {
  curology: LockIcon,
  agency: AgencyLockIcon,
} as const;

export const RxCard = ({ card }: { card: Card }) => {
  const { brand, rxCards, flags, patient } = useDataProvider(
    DATA_KEYS.productSelection,
  );
  const { isCardSelected, toggleCard, selectedRxCards } = useSelectedItemIds();
  const { setModalCard } = useProductIngredientsModalState();

  const BrandLockIcon = brandLockIcons[brand];

  const cardSelected = useMemo(
    () => isCardSelected(card),
    [card, isCardSelected],
  );

  return (
    <ProductCard>
      <ProductCardContent
        className="cur-cursor-pointer"
        data-sentry-id="Show product modal"
        onClick={() => setModalCard(card)}
        image={
          <ProductCardImageCarousel
            aspectRatio="3:2"
            card={card}
            patient={patient}
          />
        }
        meta={
          <>
            <div className="cur-flex cur-justify-between">
              <RxCardPricing rxCard={card} />
              {card?.claim?.short ? (
                <Typography
                  className="cur-bg-surface-highlight cur-px-1 cur-text-brand"
                  size="meta"
                  weight="bold"
                >
                  {card.claim.short}
                  {flags.isTwoMonthTrialWith50PctOff ? '*' : ''}
                </Typography>
              ) : null}
            </div>
            {card.metaSubtext ? (
              <Typography size="meta">{card.metaSubtext}</Typography>
            ) : null}
          </>
        }
        onKeyPress={(event) => {
          if (event.key === 'Enter') {
            setModalCard(card);
          }
        }}
        tabIndex={0}
        title={
          <div className="cur-flex cur-justify-between">
            <Typography as="p" size="h3" weight="bold">
              {card.name}
              <sup>Rx</sup>
            </Typography>
            <div className="cur-flex cur-items-center cur-text-brand">
              {selectedRxCards.length === 1 &&
                (cardSelected ? (
                  <BrandLockIcon className="cur-mr-1" size="sm" />
                ) : (
                  <Checkbox
                    checked={cardSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => {
                      toggleCard({
                        card,
                        patient,
                      });
                    }}
                  />
                ))}
              {selectedRxCards.length !== 1 && (
                <Checkbox
                  checked={cardSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => {
                    toggleCard({
                      card,
                      patient,
                    });
                  }}
                />
              )}
              {rxCards.length === 1 && (
                <Typography size="meta">Required</Typography>
              )}
            </div>
          </div>
        }
      >
        <Typography size="body">{card.bodyDescription}</Typography>
        {card.callout && <Highlight title={card.callout} />}
        <div>
          {card.ingredients && (
            <IngredientTabs ingredients={card.ingredients} />
          )}
        </div>
      </ProductCardContent>
    </ProductCard>
  );
};
